import { useState } from 'react';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Button,
  Text,
  Banner,
  BlockStack,
} from '@shopify/polaris';
import { useSearchParams } from 'react-router-dom';
import { authApi } from '../lib/api';

export function Login() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const errorParam = searchParams.get('error');
  const logoutParam = searchParams.get('logout');
  const uninstallParam = searchParams.get('uninstall');

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await authApi.sendMagicLink(email);
      setSuccess(true);
    } catch (err: unknown) {
      // Handle API error with proper message
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to send magic link. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleWebflowLogin = () => {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    window.location.href = `${apiUrl}/webflow/auth`;
  };

  return (
    <Page>
      <div
        style={{
          maxWidth: '450px',
          margin: '80px auto',
          padding: '0 16px',
        }}
      >
        <BlockStack gap="600">
          {/* Logo & Title */}
          <div style={{ textAlign: 'center' }}>
            <Text as="h1" variant="headingXl" fontWeight="bold">
              Analyzely
            </Text>
            <Text as="p" variant="bodyLg" tone="subdued">
              Google Analytics 4 for Webflow
            </Text>
          </div>

          {/* Logout Success Banner */}
          {logoutParam === 'success' && (
            <Banner
              tone="success"
              onDismiss={() => {
                searchParams.delete('logout');
                setSearchParams(searchParams);
              }}
            >
              You have been logged out successfully.
            </Banner>
          )}

          {/* Uninstall Success Banner */}
          {uninstallParam === 'success' && (
            <Banner
              tone="success"
              onDismiss={() => {
                searchParams.delete('uninstall');
                setSearchParams(searchParams);
              }}
            >
              Analyzely has been uninstalled successfully. All your data has been deleted.
            </Banner>
          )}

          {/* Error Banner */}
          {(errorParam || error) && (
            <Banner tone="critical" onDismiss={() => setError('')}>
              {errorParam === 'oauth_denied' && 'OAuth access was denied'}
              {errorParam === 'invalid_token' && 'Invalid or expired login link'}
              {errorParam === 'user_not_found' && 'User not found'}
              {!errorParam && error}
            </Banner>
          )}

          {/* Success Message */}
          {success ? (
            <Card>
              <BlockStack gap="400">
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text as="h2" variant="headingMd">
                    Check your email
                  </Text>
                  <Text as="p" tone="subdued">
                    We've sent a magic link to <strong>{email}</strong>
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    The link will expire in 15 minutes
                  </Text>
                </div>
                <Button onClick={() => setSuccess(false)}>
                  Send another link
                </Button>
              </BlockStack>
            </Card>
          ) : (
            <>
              {/* Webflow Login Button */}
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Sign in with Webflow
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Connect your Webflow account to get started
                  </Text>
                  <Button
                    variant="primary"
                    size="large"
                    fullWidth
                    onClick={handleWebflowLogin}
                  >
                    Continue with Webflow
                  </Button>
                </BlockStack>
              </Card>

              {/* Divider */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                  }}
                />
                <Text as="span" variant="bodySm" tone="subdued">
                  or
                </Text>
                <div
                  style={{
                    flex: 1,
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                  }}
                />
              </div>

              {/* Magic Link Form */}
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Sign in with email
                  </Text>
                  <FormLayout>
                    <TextField
                      label="Email address"
                      type="email"
                      value={email}
                      onChange={setEmail}
                      autoComplete="email"
                      placeholder="you@example.com"
                    />
                    <Button
                      variant="primary"
                      size="large"
                      fullWidth
                      loading={isLoading}
                      onClick={handleSubmit}
                    >
                      Send magic link
                    </Button>
                  </FormLayout>
                </BlockStack>
              </Card>
            </>
          )}

          {/* Footer Links */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '24px',
              paddingTop: '24px',
            }}
          >
            <a
              href="mailto:support@analyzely.app"
              style={{
                color: '#303030',
                textDecoration: 'none',
                fontSize: '14px',
              }}
            >
              Contact us
            </a>
            <a
              href="https://analyzely.app/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#303030',
                textDecoration: 'none',
                fontSize: '14px',
              }}
            >
              Terms & Conditions
            </a>
            <a
              href="https://analyzely.app/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#303030',
                textDecoration: 'none',
                fontSize: '14px',
              }}
            >
              Privacy Policy
            </a>
          </div>
        </BlockStack>
      </div>
    </Page>
  );
}
